import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import apiClient from "../client";

/** Mirrors Scr.Enums.TradingStrategySignalAction. Sent as a number, so read it through this. */
export const TradingStrategySignalAction = {
  Unknown: 0,
  Buy: 1,
  Exit: 2,
} as const;

/** A strategy whose signals the customer receives. */
export interface ReceivedSignalStrategyDto {
  id: string;
  name: string;
  /** Its newest signal, which says where it stands now. Null until it sends one. */
  latestSignal: RelatedTradingStrategySignalDto | null;
  /** Whether the customer follows it. */
  following: boolean;
}

export interface TradingStrategySignalDto {
  id: string;
  tradingStrategyId: string;
  tradingStrategyName: string;
  action: number;
  /** The instrument to trade, which is not always the one the strategy is charted on. */
  symbol: string;
  receivedAt: string;
  /** The strategy's trade this signal opens or closes. */
  trade: SignalTradeDto;
}

export interface TradingStrategySignalsDto {
  /** The strategies whose signals the customer receives, whatever the filters. */
  strategies: ReceivedSignalStrategyDto[];
  /** One page of the signals that match the filters, newest first. */
  signals: TradingStrategySignalDto[];
  /** How many signals match the filters, across every page. */
  totalCount: number;
  /** Whether older signals match beyond this page. */
  hasMore: boolean;
}

export interface SignalFeedFilters {
  strategyId: string | null;
  /** Only signals from strategies the customer follows. */
  following: boolean;
}

const SIGNAL_PAGE_SIZE = 50;

/** Another signal from the same strategy. */
export interface RelatedTradingStrategySignalDto {
  id: string;
  action: number;
  symbol: string;
  receivedAt: string;
}

/** A buy opens the trade and an exit of the same instrument closes it. */
export interface SignalTradeDto {
  /** For an exit, the buy it closes. Null for a buy, or when that buy can't be named. */
  openedBy: RelatedTradingStrategySignalDto | null;
  /** For a buy, the exit that closed it. Null for an exit, or when no exit has closed it. */
  closedBy: RelatedTradingStrategySignalDto | null;
  /** An exit whose buy came before the strategy's signals started here. */
  openedBeforeSignalsStarted: boolean;
  /** A buy with no later signal of its instrument. */
  stillOpen: boolean;
}

export interface TradingStrategySignalDetailDto {
  id: string;
  tradingStrategyId: string;
  tradingStrategyName: string;
  action: number;
  symbol: string;
  receivedAt: string;
  /** Null when this is the strategy's latest signal. */
  newerSignalId: string | null;
  /** Null when this is the strategy's first signal. */
  olderSignalId: string | null;
  /** The strategy's latest signal. Null when this is it. */
  latestSignal: RelatedTradingStrategySignalDto | null;
  /** The strategy's trade this signal opens or closes. */
  trade: SignalTradeDto;
  /** This signal and the strategy's signals just before it, newest first. */
  recentSignals: RelatedTradingStrategySignalDto[];
}

export const SIGNALS_KEY = ["trading-strategy-signals"] as const;

/** The dashboard card's read: the latest signals from the strategies the customer follows. */
export function useTradingStrategySignals() {
  return useQuery<TradingStrategySignalsDto>({
    queryKey: [...SIGNALS_KEY, "following"],
    queryFn: async () => {
      const res = await apiClient.get<TradingStrategySignalsDto>("/trading-strategy-signals", {
        params: { following: true },
      });
      return res.data;
    },
  });
}

/**
 * The Signals page's list, filtered and a page at a time. Each page after the first is fetched with the id of the
 * last signal already loaded; the previous filter's signals stay on screen while a new filter loads.
 */
export function useTradingStrategySignalFeed(filters: SignalFeedFilters) {
  return useInfiniteQuery({
    queryKey: [
      ...SIGNALS_KEY,
      "feed",
      filters.strategyId ?? "all",
      filters.following ? "following" : "everything",
    ],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await apiClient.get<TradingStrategySignalsDto>("/trading-strategy-signals", {
        params: {
          limit: SIGNAL_PAGE_SIZE,
          ...(filters.strategyId ? { strategyId: filters.strategyId } : {}),
          ...(filters.following ? { following: true } : {}),
          ...(pageParam ? { startingAfter: pageParam } : {}),
        },
      });
      return res.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.signals[lastPage.signals.length - 1]?.id : undefined,
    placeholderData: keepPreviousData,
  });
}

export function useTradingStrategySignal(signalId: string) {
  return useQuery<TradingStrategySignalDetailDto>({
    queryKey: [...SIGNALS_KEY, signalId],
    queryFn: async () => {
      const res = await apiClient.get<TradingStrategySignalDetailDto>(
        `/trading-strategy-signals/${encodeURIComponent(signalId)}`,
      );
      return res.data;
    },
    // A missing signal answers 404 every time, so asking again only delays saying so.
    retry: false,
  });
}
